import type { FeatureConfig } from '@skynet/feature-config';
import { CrownOutlined } from '@ant-design/icons';
import { TENANT_DETAIL_ROUTE_PATH, TENANTS_LIST_URL, TENANTS_ROUTE_PATH } from './routes';

/**
 * Feature manifest for the admin Tenants list and tenant detail pages. The
 * dashboard shell reads this single source of truth instead of hardcoding the
 * route, navigation entry, header label and Cerbos action across its routing,
 * navigation, header and access-control modules.
 *
 * `route.load` and `detailRoute.load` are dynamic-import thunks, so importing
 * this config for its nav/header metadata never pulls the page implementations
 * into the main bundle.
 *
 * The route path, nav url and WS-login skip path come from this package's own
 * `./routes`, which also supplies the URLs the page hands to `@skynet/sky-tenant`'s
 * components. The config imports nothing from `@skynet/sky-tenant`, so reading it
 * never drags the Tenants UI onto the boot path.
 * `section`, `nav.key` and `cerbosAction` stay literal: they are the identifier
 * the Cerbos policy is written against (`read:admin:tenants`), and renaming any
 * of them hides the nav item until the policy is renamed too.
 */
export const tenantsConfig = {
  domain: 'Admin',
  section: 'tenants',
  route: {
    path: TENANTS_ROUTE_PATH,
    load: async () => ({ default: (await import('./Tenants')).Tenants }),
  },
  detailRoute: {
    path: TENANT_DETAIL_ROUTE_PATH,
    load: async () => ({ default: (await import('./TenantPage')).TenantPage }),
  },
  nav: {
    key: 'admin.tenants',
    label: 'Tenants',
    icon: <CrownOutlined />,
    url: TENANTS_LIST_URL,
  },
  header: { label: 'TENANTS' },
  cerbosAction: 'read:admin:tenants',
  skipWsLoginPaths: [TENANTS_LIST_URL],
} as const satisfies FeatureConfig;
